import { useEffect, useState } from "react";
import type { UsIngestEntry, UsAnalytics, UsMarketInfo, UsWeek, UsRulesKey, UsTrades, UsBacktestReport, UsScanBook, UsMarketTrades, BacktestReport, GroupCode, JournalRow, Me, ScannerRow, SeriesResponse, Timeframe, TrendMode, UserView, ActiveTrade, GrabitTicker, GrabitForecast, GrabitFutoi } from "./types";
import { DEFAULTS, type ParamSet } from "./lib/settings";

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

/** Событие для auth-хранилища: сервер ответил «не вошли» (401) или «сначала смените пароль». */
export const AUTH_EVENT = "insidera:auth";

async function request<T>(method: string, url: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const res = await fetch(url, {
    method,
    signal,
    credentials: "same-origin",
    headers: body === undefined ? { Accept: "application/json" } : { Accept: "application/json", "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = res.status === 401 ? "Нужно войти" : res.status === 403 ? "Нет доступа" : res.statusText;
    let code: string | undefined;
    try {
      const b = await res.json();
      detail = b.detail ?? b.message ?? detail;
      code = b.code;
    } catch {
      /* тело не JSON */
    }
    if ((res.status === 401 && !url.startsWith("/api/auth/")) || code === "PASSWORD_CHANGE_REQUIRED") {
      window.dispatchEvent(new CustomEvent(AUTH_EVENT, { detail: { status: res.status, code } }));
    }
    throw new ApiError(detail, res.status);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  return request<T>("GET", url, undefined, signal);
}

export interface SeriesQuery {
  code: string;
  tf: Timeframe;
  smart: GroupCode;
  cotShort: number;
  cotLong: number;
  willco: number;
  oi: number;
  trend: TrendMode;
  smoothExpiry: boolean;
  /** Набор параметров из «Настроек» (окна COT/WILLCO/OI выше берут верх над набором). */
  params?: ParamSet;
}

function seriesParams(q: SeriesQuery): URLSearchParams {
  const s = q.params ?? DEFAULTS;
  return new URLSearchParams({
    tf: q.tf,
    smart: q.smart,
    cotShort: String(q.cotShort),
    cotLong: String(q.cotLong),
    willco: String(q.willco),
    oi: String(q.oi),
    trend: q.trend,
    smoothExpiry: String(q.smoothExpiry),
    sma: String(s.sma),
    smaB: String(s.smaB),
    extreme: String(s.extreme),
    progo: String(s.progo),
    progoDiv: String(s.progoDiv),
    willcoMode: s.willcoMode,
    adjust: s.adjust,
    priceOverMa: String(s.priceOverMa),
    flatMaAllowsBuy: String(s.flatMaAllowsBuy),
    oiInM19ForIndex: String(s.oiInM19ForIndex),
    channel: String(s.channel),
    maCross: String(s.maCross),
    csHigh: String(s.csHigh),
    csLow: String(s.csLow),
    csStrong: String(s.csStrong),
    seasonTol: String(s.seasonTol),
    jilerMode: s.jilerMode,
    jilerYears: String(s.jilerYears),
    maxLoss: String(s.maxLoss),
    pubLag: String(s.pubLag ?? DEFAULTS.pubLag),
    costPct: String(s.costPct ?? DEFAULTS.costPct),
    seasonMinYears: String(s.seasonMinYears ?? DEFAULTS.seasonMinYears),
    jilerMinYears: String(s.jilerMinYears ?? DEFAULTS.jilerMinYears),
    jilerResearch: String(s.jilerResearch ?? DEFAULTS.jilerResearch),
    cotHigh: String(s.cotHigh ?? DEFAULTS.cotHigh),
    cotLow: String(s.cotLow ?? DEFAULTS.cotLow),
    willcoHigh: String(s.willcoHigh ?? DEFAULTS.willcoHigh),
    willcoLow: String(s.willcoLow ?? DEFAULTS.willcoLow),
    willcoStrong: String(s.willcoStrong ?? DEFAULTS.willcoStrong),
    oiLow: String(s.oiLow ?? DEFAULTS.oiLow),
    oiHigh: String(s.oiHigh ?? DEFAULTS.oiHigh),
  });
}

export const api = {
  scanner: (signal?: AbortSignal) => getJson<ScannerRow[]>("/api/ru/scanner", signal),
  backtest: (signal?: AbortSignal) => getJson<BacktestReport>("/api/ru/backtest", signal),
  demoScanner: (signal?: AbortSignal) => getJson<ScannerRow[]>("/api/demo/scanner", signal),
  demoInfo: (signal?: AbortSignal) => getJson<{ markets: string[] }>("/api/demo/info", signal),
  demoSeries: (q: SeriesQuery, signal?: AbortSignal) => {
    const p = new URLSearchParams({
      tf: q.tf,
      smart: q.smart,
      cotShort: String(q.cotShort),
      willco: String(q.willco),
      oi: String(q.oi),
      trend: q.trend,
      smoothExpiry: String(q.smoothExpiry),
    });
    return getJson<SeriesResponse>(`/api/demo/markets/${encodeURIComponent(q.code)}/series?${p}`, signal);
  },

  me: () => getJson<Me>("/api/auth/me"),
  login: (username: string, password: string) => request<Me>("POST", "/api/auth/login", { username, password }),
  logout: () => request<Me>("POST", "/api/auth/logout"),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<Me>("POST", "/api/auth/password", { currentPassword, newPassword }),

  users: (signal?: AbortSignal) => getJson<UserView[]>("/api/admin/users", signal),
  createUser: (username: string, password: string, role: "USER" | "ADMIN") =>
    request<UserView>("POST", "/api/admin/users", { username, password, role }),
  resetPassword: (id: number, password: string) => request<UserView>("POST", `/api/admin/users/${id}/password`, { password }),
  deleteUser: (id: number) => request<void>("DELETE", `/api/admin/users/${id}`),

  grabitTickers: (signal?: AbortSignal) => getJson<GrabitTicker[]>("/api/grabit/tickers", signal),
  grabitForecast: (ticker: string, signal?: AbortSignal) =>
    getJson<GrabitForecast>(`/api/grabit/forecast/${encodeURIComponent(ticker)}`, signal),
  grabitFutoi: (ticker: string, code: string, signal?: AbortSignal) =>
    getJson<GrabitFutoi>(`/api/grabit/futoi/${encodeURIComponent(ticker)}?code=${encodeURIComponent(code)}`, signal),
  grabitTelegram: (signal?: AbortSignal) => getJson<{ url: string | null }>("/api/grabit/telegram", signal),

  /** Одноразовая ссылка t.me/<бот>?start=<код> для подписки на уведомления. */
  telegramLink: () => request<{ url: string; expiresAt: string }>("POST", "/api/telegram/link"),

  series: (q: SeriesQuery, signal?: AbortSignal) =>
    getJson<SeriesResponse>(`/api/ru/markets/${encodeURIComponent(q.code)}/series?${seriesParams(q)}`, signal),

  journal: (days: number, signal?: AbortSignal) => getJson<JournalRow[]>(`/api/ru/journal?days=${days}`, signal),
  activeTrades: (rules: "setup" | "canon", signal?: AbortSignal) =>
    getJson<ActiveTrade[]>(`/api/ru/active-trades?rules=${rules}`, signal),

  // Рынок США (CFTC) — отдельно от Мосбиржи
  usMarkets: (signal?: AbortSignal) => getJson<UsMarketInfo[]>("/api/us/markets", signal),
  usIngest: (signal?: AbortSignal) => getJson<UsIngestEntry[]>("/api/us/ingest", signal),
  usScanner: (signal?: AbortSignal) => getJson<UsAnalytics[]>("/api/us/scanner", signal),
  usAnalytics: (code: string, signal?: AbortSignal) =>
    getJson<UsAnalytics>(`/api/us/markets/${encodeURIComponent(code)}/analytics`, signal),
  usCot: (code: string, signal?: AbortSignal) =>
    getJson<UsWeek[]>(`/api/us/markets/${encodeURIComponent(code)}/cot`, signal),
  usTrades: (rules: UsRulesKey, signal?: AbortSignal) => getJson<UsTrades>(`/api/us/trades?rules=${rules}`, signal),
  usBacktest: (rules: UsRulesKey, signal?: AbortSignal) =>
    getJson<UsBacktestReport>(`/api/us/backtest?rules=${rules}`, signal),
  usScannerBook: (rules: UsRulesKey, signal?: AbortSignal) =>
    getJson<UsScanBook[]>(`/api/us/scanner/book?rules=${rules}`, signal),
  usMarketTrades: (code: string, rules: UsRulesKey, from: string, signal?: AbortSignal) =>
    getJson<UsMarketTrades>(`/api/us/markets/${encodeURIComponent(code)}/trades?rules=${rules}&from=${from}`, signal),
};

export interface Loadable<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
}

/** Загрузка данных с отменой устаревших запросов. */
export function useLoad<T>(load: (signal: AbortSignal) => Promise<T>, deps: unknown[]): Loadable<T> {
  const [state, setState] = useState<Loadable<T>>({ data: null, error: null, loading: true });
  useEffect(() => {
    const ctrl = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null }));
    load(ctrl.signal)
      .then((data) => setState({ data, error: null, loading: false }))
      .catch((e: unknown) => {
        if (ctrl.signal.aborted) return;
        const msg = e instanceof Error ? e.message : String(e);
        setState({ data: null, error: msg, loading: false });
      });
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}
